import { useEffect, useRef, useState } from "react";
import { Timer, ChefHat, BellRing, Play, Check, Bike, ShoppingBag, UtensilsCrossed, LayoutGrid, CheckCircle2, ArrowRight, Trash2, ChevronDown, Volume2, VolumeX, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, useApi, errMsg } from "@/pos/lib/api";
import { rm } from "@/pos/lib/format";
import { Printer } from "lucide-react";
import { printKitchen, printOnline } from "@/pos/lib/print";
import { getRole } from "@/pos/lib/printer";
import { useNow, elapsed, clock, beep, TYPE_LABEL } from "@/pos/lib/restaurant";

const TYPE_ICON = { dine_in: UtensilsCrossed, takeaway: ShoppingBag, delivery: Bike };
const COLS = {
  pending: { title: "Order Baru", icon: Timer, wrap: "bg-rose-50 border-rose-100", card: "border-rose-200", num: "text-rose-600", dot: "bg-rose-500", pill: "bg-rose-500" },
  preparing: { title: "Sedang Dimasak", icon: ChefHat, wrap: "bg-amber-50 border-amber-100", card: "border-amber-200", num: "text-amber-600", dot: "bg-amber-400", pill: "bg-amber-400" },
  ready: { title: "Siap", icon: BellRing, wrap: "bg-green-50 border-green-100", card: "border-green-300", num: "text-green-600", dot: "bg-green-500", pill: "bg-green-500" },
};
const DONE_LABEL = { dine_in: "Tanda Dihidang", delivery: "Serah ke Rider", takeaway: "Tanda Diambil" };

function OrderCard({ o, col, now, onStatus }) {
  const c = COLS[col];
  const I = TYPE_ICON[o.type];
  return (
    <div className={`animate-rise rounded-2xl border-2 bg-white p-4 ${c.card}`} data-testid={`kitchen-order-${o.number}`}>
      <div className="flex items-start justify-between gap-2">
        <span className={`font-heading text-lg font-bold ${c.num}`}>#{o.number}</span>
        <span className="flex items-center gap-1.5 text-sm font-medium"><I size={16} />{TYPE_LABEL[o.type]}{o.table_number && <span className="text-xs text-slate-500">· Meja {o.table_number}</span>}</span>
        <span className="flex items-center gap-1 text-xs text-slate-500">{clock(o.created_at)}<button title="Cetak semula" onClick={() => (o.source === "online" && getRole() !== "dapur" ? printOnline(o) : printKitchen(o, { manual: true }))} className="icon-btn" data-testid={`kitchen-print-${o.number}`}><Printer size={14} /></button></span>
      </div>
      {o.customer_name && <div className="mt-0.5 text-xs text-slate-500">{o.customer_name}</div>}
      <ul className="mt-3 space-y-1.5 text-sm">
        {o.items.map((it, i) => (
          <li key={i} className="flex gap-2"><span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${c.dot}`} /><span>{it.qty} × {it.name}{it.note && <span className="block text-xs font-medium text-amber-700">» {it.note}</span>}</span></li>
        ))}
      </ul>
      {o.note && <div className="mt-2 rounded-lg bg-slate-50 px-2 py-1 text-xs text-slate-600">{o.note}</div>}
      <div className="mt-4">
        {col === "pending" && <button onClick={() => onStatus(o, "preparing")} className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border-2 border-rose-400 font-semibold text-rose-600 transition-colors hover:bg-rose-50" data-testid={`kitchen-start-${o.number}`}><Play size={16} /> Mula Masak</button>}
        {col === "preparing" && (
          <div className="grid grid-cols-2 gap-2">
            <div className="flex h-11 items-center justify-center gap-1.5 rounded-xl border-2 border-amber-300 bg-amber-50 font-mono font-semibold text-amber-700" data-testid={`kitchen-timer-${o.number}`}><Timer size={16} />{elapsed(o.started_at, now)}</div>
            <button onClick={() => onStatus(o, "ready")} className="flex h-11 items-center justify-center gap-1.5 rounded-xl border-2 border-amber-300 font-semibold text-amber-700 transition-colors hover:bg-amber-100" data-testid={`kitchen-ready-${o.number}`}><Check size={16} /> Siap</button>
          </div>
        )}
        {col === "ready" && <button onClick={() => onStatus(o, "completed")} className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-green-500 font-semibold text-white transition-colors hover:bg-red-600" data-testid={`kitchen-complete-${o.number}`}><Play size={16} /> {DONE_LABEL[o.type]}</button>}
      </div>
    </div>
  );
}

function Column({ col, orders, now, onStatus }) {
  const [all, setAll] = useState(false);
  const c = COLS[col];
  const shown = all ? orders : orders.slice(0, 3);
  return (
    <section className={`flex flex-col rounded-3xl border p-3 ${c.wrap}`} data-testid={`kitchen-col-${col}`}>
      <h2 className="mb-3 flex items-center gap-2 px-1 font-heading text-lg font-bold"><c.icon size={20} className={c.num} />{c.title}<span className={`grid h-6 min-w-6 place-items-center rounded-full px-1.5 text-xs text-white ${c.pill}`}>{orders.length}</span></h2>
      <div className="space-y-3">{shown.map((o) => <OrderCard key={o.id} o={o} col={col} now={now} onStatus={onStatus} />)}</div>
      {!orders.length && <div className="py-10 text-center text-sm text-slate-400">Tiada order</div>}
      {orders.length > 3 && <button onClick={() => setAll(!all)} className="mt-3 flex items-center justify-center gap-1 text-sm text-slate-600" data-testid={`kitchen-more-${col}`}>{all ? "Tunjuk sedikit" : `+ ${orders.length - 3} Lagi Order`} <ChevronDown size={15} className={all ? "rotate-180" : ""} /></button>}
    </section>
  );
}

function Summary({ d, onViewAll }) {
  const boxes = [["pending", "Baru", "text-rose-600", d.pending.length], ["preparing", "Dimasak", "text-amber-500", d.preparing.length], ["ready", "Siap", "text-green-600", d.ready.length], ["done", "Selesai", "text-slate-700", d.completed_count]];
  return (
    <aside className="card-surface flex flex-col p-4" data-testid="kitchen-summary">
      <h3 className="font-heading font-bold">Ringkasan Order</h3>
      <div className="mt-3 grid grid-cols-2 gap-3">
        {boxes.map(([k, l, tone, n]) => (
          <div key={k} className="rounded-2xl border border-slate-100 bg-slate-50/60 py-5 text-center" data-testid={`kitchen-count-${k}`}><div className={`font-heading text-3xl font-extrabold ${tone}`}>{String(n).padStart(2, "0")}</div><div className={`text-sm ${tone}`}>{l}</div></div>
        ))}
      </div>
      <h4 className="mt-5 text-sm font-bold">Baru Selesai</h4>
      <div className="mt-2 flex-1 space-y-2" data-testid="kitchen-recent-list">
        {d.recent_completed.map((o) => (
          <div key={o.id} className="flex items-center gap-2 rounded-xl border border-slate-100 p-2.5 text-sm">
            <CheckCircle2 size={18} className="text-red-500" />
            <div className="flex-1"><b>#{o.number}</b><div className="text-xs text-slate-500">{TYPE_LABEL[o.type]}{o.table_number && ` · Meja ${o.table_number}`}</div></div>
            <div className="text-right text-xs text-slate-500">{clock(o.completed_at)}<div className="font-mono font-semibold text-slate-800">{rm(o.total)}</div></div>
          </div>
        ))}
        {!d.recent_completed.length && <div className="py-4 text-center text-xs text-slate-400">Belum ada</div>}
      </div>
      <button onClick={onViewAll} className="mt-4 flex h-11 items-center justify-center gap-2 rounded-xl border-2 border-red-500 font-semibold text-red-700 hover:bg-red-50" data-testid="kitchen-view-all-btn">Lihat Semua Selesai <ArrowRight size={16} /></button>
    </aside>
  );
}

function NoteBar({ d, onClear, reload }) {
  const [note, setNote] = useState(d.note);
  useEffect(() => setNote(d.note), [d.note]);
  const save = async () => { if (note === d.note) return; try { await api.put("/kitchen/note", { note }); toast.success("Nota dapur disimpan"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const tag = "rounded-md bg-red-600 px-2 py-0.5 font-mono text-xs font-bold text-white";
  return (
    <div className="card-surface mt-4 flex flex-col gap-3 p-3 lg:flex-row lg:items-center">
      <div className="flex-1"><div className="text-xs text-slate-500">Nota Dapur</div>
        <input value={note} onChange={(e) => setNote(e.target.value)} onBlur={save} placeholder="cth: Hidang Meja 06 dahulu, kurang pedas" className="w-full bg-transparent text-sm outline-none" data-testid="kitchen-note-input" /></div>
      <div className="text-center"><div className="text-xs text-slate-500">Prestasi Hari Ini</div>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">Selesai <span className={tag} data-testid="kitchen-perf-completed">{d.completed_count}</span> Purata Masak <span className={tag} data-testid="kitchen-perf-avg">{d.avg_prep_min} min</span> Kecekapan <span className={tag} data-testid="kitchen-perf-eff">{d.efficiency}%</span></div></div>
      <button onClick={onClear} className="flex items-center justify-center gap-2 rounded-xl border-2 border-rose-300 px-4 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-50" data-testid="kitchen-clear-btn"><Trash2 size={15} /> Kosongkan Selesai</button>
    </div>
  );
}

export default function Kitchen() {
  const { data, reload } = useApi("/kitchen");
  const now = useNow(1000);
  const [type, setType] = useState("all");
  const [sound, setSound] = useState(true);
  const [viewAll, setViewAll] = useState(false);
  const seen = useRef(null);

  useEffect(() => { const t = setInterval(reload, 5000); return () => clearInterval(t); }, [reload]);
  useEffect(() => {
    if (!data) return;
    const ids = new Set(data.pending.map((o) => o.id));
    if (seen.current && [...ids].some((id) => !seen.current.has(id))) { if (sound) beep(); toast.info("Order baru masuk!"); }
    seen.current = ids;
  }, [data, sound]);

  if (!data) return <div className="p-8 text-slate-500">Memuatkan...</div>;
  const f = (list) => list.filter((o) => type === "all" || o.type === type);
  const onStatus = async (o, status) => { try { await api.post(`/orders/${o.id}/status`, { status }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const clear = async () => { try { await api.post("/kitchen/clear"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const tabs = [["all", "Semua Order", LayoutGrid], ["dine_in", "Makan Sini", UtensilsCrossed], ["takeaway", "Bungkus", ShoppingBag], ["delivery", "Delivery", Bike]];

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <h1 className="font-heading text-2xl font-extrabold" data-testid="page-title">Paparan Dapur</h1>
        <div className="no-scrollbar flex gap-1 overflow-x-auto rounded-2xl border border-slate-200 bg-white p-1">
          {tabs.map(([k, l, I]) => (
            <button key={k} onClick={() => setType(k)} data-testid={`kitchen-tab-${k}`} className={`flex shrink-0 items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${type === k ? "bg-[#C81E1E] text-white" : "text-slate-600 hover:bg-slate-50"}`}><I size={16} />{l}</button>
          ))}
        </div>
        <button onClick={() => setSound(!sound)} className="btn-outline" data-testid="kitchen-sound-toggle">{sound ? <Volume2 size={16} /> : <VolumeX size={16} />} Bunyi {sound ? "Hidup" : "Mati"}</button>
      </div>
      <div className="grid gap-4 xl:grid-cols-[1fr_1fr_1fr_300px] md:grid-cols-3">
        {["pending", "preparing", "ready"].map((c) => <Column key={c} col={c} orders={f(data[c])} now={now} onStatus={onStatus} />)}
        <div className="md:col-span-3 xl:col-span-1"><Summary d={data} onViewAll={() => setViewAll(true)} /></div>
      </div>
      <NoteBar d={data} onClear={clear} reload={reload} />
      <Dialog open={viewAll} onOpenChange={setViewAll}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto" data-testid="kitchen-completed-dialog">
          <DialogHeader><DialogTitle className="font-heading">Order Selesai Hari Ini ({data.completed_count})</DialogTitle></DialogHeader>
          {data.completed_today.map((o) => (
            <div key={o.id} className="flex justify-between border-b border-slate-100 py-2 text-sm"><span><b>#{o.number}</b> · {TYPE_LABEL[o.type]}{o.table_number && ` · Meja ${o.table_number}`}<span className="block text-xs text-slate-500">{o.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}</span></span><span className="text-right text-xs">{clock(o.completed_at)}<span className="block font-mono font-semibold">{rm(o.total)}</span></span></div>
          ))}
          {!data.completed_today.length && <div className="py-6 text-center text-sm text-slate-500"><X className="mx-auto mb-1" />Belum ada</div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
